import { BookOpen, Play, Camera, CheckCircle2 } from 'lucide-react';
import React from 'react';

interface VideoExample {
  url: string;
  thumbnail_url: string;
  why_it_works: string;
}

interface ChallengePreviewProps {
  dayNumber: number;
  title: string;
  subtitle: string;
  description: string;
  proTip?: string;
  actionDescription: string;
  checklistItems: string[];
  gemsReward: number;
  videoExamples: VideoExample[];
}

/** Parse **bold** and *italic* markers into React nodes, preserving line breaks via whitespace-pre-line on the parent */
function renderFormattedText(text: string): React.ReactNode[] {
  // Split by bold/italic markers
  const parts: React.ReactNode[] = [];
  // Regex: **bold** or *italic*
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      // Bold
      parts.push(<strong key={key++} className="font-bold">{match[2]}</strong>);
    } else if (match[3]) {
      // Italic
      parts.push(<em key={key++} className="italic">{match[3]}</em>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

export default function ChallengePreview({
  dayNumber, title, subtitle, description, proTip,
  actionDescription, checklistItems, gemsReward, videoExamples,
}: ChallengePreviewProps) {
  return (
    <div className="w-[375px] mx-auto bg-white rounded-[2rem] shadow-xl border-[8px] border-gray-800 overflow-hidden flex flex-col" style={{ height: 812 }}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 py-2 text-xs font-semibold text-gray-800">
        <span>1:00</span>
        <div className="flex items-center gap-1">
          <span>●●●</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-5 pb-3">
        <h2 className="text-[28px] font-extrabold text-gray-900 leading-tight">Challenge</h2>
        <div className="flex items-center gap-1 bg-white border rounded-full px-3 py-1 text-sm font-semibold text-gray-700">
          💎 {gemsReward}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-0">
        {/* Lesson card */}
        <div className="border-l-4 rounded-xl bg-white p-1 mb-4" style={{ borderColor: '#e91e8c' }}>
          {/* Day circle + title */}
          <div className="flex items-center gap-3 mb-5 px-3 pt-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0" style={{ background: '#e91e8c' }}>
              {dayNumber}
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 text-lg leading-tight">{title || 'Challenge Title'}</p>
              <p className="text-sm mt-0.5 font-medium" style={{ color: '#e91e8c' }}>{subtitle || 'Subtitle'}</p>
            </div>
          </div>

          <hr className="mb-5 border-gray-100 mx-3" />

          {/* LEARN */}
          <div className="mb-5 px-3">
            <div className="flex items-center gap-1.5 mb-3">
              <BookOpen className="h-4 w-4" style={{ color: '#e91e8c' }} />
              <span className="text-xs font-bold tracking-wider" style={{ color: '#e91e8c' }}>LEARN</span>
            </div>
            <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">
              {description ? renderFormattedText(description) : 'Description content will appear here...'}
            </p>
          </div>

          {/* Pro Tip */}
          {proTip && (
            <div className="mx-3 mb-5">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-sm">💡</span>
                <span className="text-xs font-bold tracking-wider" style={{ color: '#e91e8c' }}>PRO TIP</span>
              </div>
              <div className="border-l-4 rounded-lg p-4" style={{ borderColor: '#f5c97e', background: '#fef7ec' }}>
                <p className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-line">
                  {renderFormattedText(proTip)}
                </p>
              </div>
            </div>
          )}

          {/* EXAMPLE VIDEOS */}
          {videoExamples.length > 0 && (
            <div className="mb-5 px-3">
              {videoExamples.map((ve, idx) => (
                <div key={idx} className="flex gap-3 mb-6 last:mb-0" style={{ minHeight: 240 }}>
                  {/* Thumbnail */}
                  <div className="w-[42%] rounded-xl bg-gray-200 overflow-hidden relative shrink-0 self-stretch">
                    {ve.thumbnail_url ? (
                      <img src={ve.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Camera className="h-8 w-8" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center">
                        <Play className="h-6 w-6 text-gray-700 ml-0.5" />
                      </div>
                    </div>
                  </div>
                  {/* Why it works */}
                  <div className="flex-1 bg-gray-50 rounded-xl p-4 self-stretch flex flex-col">
                    <p className="text-xs font-bold tracking-wider mb-2" style={{ color: '#e91e8c' }}>WHY IT WORKS</p>
                    <p className="text-[14px] text-gray-600 leading-relaxed whitespace-pre-line flex-1">
                      {ve.why_it_works ? renderFormattedText(ve.why_it_works) : 'Explanation...'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* YOUR DAILY CHALLENGE */}
          <div className="mb-5 px-3">
            <div className="flex items-center gap-1.5 mb-3">
              <Camera className="h-4 w-4" style={{ color: '#e91e8c' }} />
              <span className="text-xs font-bold tracking-wider" style={{ color: '#e91e8c' }}>YOUR DAILY CHALLENGE</span>
            </div>
            <p className="text-[15px] text-gray-700 mb-3 whitespace-pre-line">
              {actionDescription ? renderFormattedText(actionDescription) : 'Action description...'}
            </p>
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-start gap-2 mb-2.5">
                <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#e91e8c' }} />
                <span className="text-[14px] text-gray-700">{renderFormattedText(item)}</span>
              </div>
            ))}
          </div>

          {/* Submit button */}
          <div className="px-3 pb-3">
            <button className="w-full py-3.5 rounded-full text-white font-bold text-sm flex items-center justify-center gap-2" style={{ background: '#e91e8c' }}>
              🎬 Submit Day {dayNumber} Video
            </button>
          </div>
        </div>
      </div>

      {/* Bottom nav */}
      <div className="border-t bg-white px-4 py-2 flex justify-around text-[10px] text-gray-400">
        <div className="flex flex-col items-center" style={{ color: '#e91e8c' }}>
          <span className="text-lg">🔥</span>
          <span className="font-semibold">Challenge</span>
        </div>
        <div className="flex flex-col items-center"><span className="text-lg">📢</span><span>Campaigns</span></div>
        <div className="flex flex-col items-center"><span className="text-lg">🏠</span><span>Home</span></div>
        <div className="flex flex-col items-center"><span className="text-lg">🧰</span><span>Toolkit</span></div>
        <div className="flex flex-col items-center"><span className="text-lg">•••</span><span>More</span></div>
      </div>
    </div>
  );
}
